/* useAuth — gestion del estado de autenticacion via localStorage + Sanctum API */
import { useState, useEffect, useCallback } from 'react';

const TOKEN_KEY = 'auth_token';
const USER_KEY  = 'auth_user';

function getStored() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user  = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return { token, user };
  } catch { return { token: null, user: null }; }
}

// Bus de eventos global simple para sincronizar todos los componentes
const listeners = new Set();
function notify() { listeners.forEach(fn => fn()); }

/** Notificar cambios de sesion hechos fuera del hook (ej. AuthModal). */
export function notifyAuthChange() { notify(); }

export function useAuth() {
  const [state, setState] = useState(getStored);

  useEffect(() => {
    const handler = () => setState(getStored());
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok || !data.exito) throw new Error(data.mensaje || 'Credenciales incorrectas');
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.usuario));
    notify();
    return data;
  }, []);

  /**
   * Registrar cliente. Acepta el objeto completo de datos del formulario:
   * { name, email, password, cedula, telefono, direccion, departamento_id, ciudad_id }
   */
  const register = useCallback(async (datos, email, password) => {
    // Compatibilidad: acepta un objeto completo o la firma legada (name, email, password)
    const payload = typeof datos === 'string'
      ? { name: datos, email, password }
      : datos;
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.exito) {
      const msg = data.errors
        ? Object.values(data.errors).flat().join('. ')
        : data.mensaje || data.message || 'Error al registrar';
      throw new Error(msg);
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.usuario));
    notify();
    return data;
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    notify();
  }, []);

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    try {
      const res = await fetch('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { logout(); return null; }
      const data = await res.json();
      const user = data.usuario || data.user || data.datos || data;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      notify();
      return user;
    } catch { return null; }
  }, [logout]);

  return {
    user: state.user,
    token: state.token,
    isLoggedIn: !!state.token,
    login,
    register,
    logout,
    refreshProfile,
  };
}
